import React, { Suspense, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { useAuth } from './contexts/AuthContext';
import { useNotification } from './contexts/NotificationContext';
import ErrorLogger from './utils/errorLogger';


import LoadingSpinner from './components/Common/LoadingSpinner';
import OfflineIndicator from './components/Common/OfflineIndicator';
import Sidebar from './components/Layout/Sidebar';
import ProtectedRoute from './components/Common/ProtectedRoute';
import { PERMISSIONS } from './constants/permissions';

const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Login = React.lazy(() => import('./pages/Auth/Login'));
const Projects = React.lazy(() => import('./pages/Projects'));
const ProjectDetail = React.lazy(() => import('./pages/Projects/ProjectDetail'));
const Customers = React.lazy(() => import('./pages/Customers'));
const CustomerDetail = React.lazy(() => import('./pages/Customers/CustomerDetail'));
const Documents = React.lazy(() => import('./pages/Documents'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Profile = React.lazy(() => import('./pages/Profile'));
const UserManagement = React.lazy(() => import('./pages/Users/UserManagement'));
const ActivityLogs = React.lazy(() => import('./pages/Admin/ActivityLogs'));
const ApprovalCenter = React.lazy(() => import('./pages/Admin/ApprovalCenter'));
const ApprovalDetails = React.lazy(() => import('./pages/ApprovalDetails'));
const MaintenanceMode = React.lazy(() => import('./pages/MaintenanceMode'));
const UtilityManagement = React.lazy(() => import('./pages/Utilities/UtilityManagement'));
const ContractStudio = React.lazy(() => import('./pages/Utilities/ContractStudio'));
const DebtManagement = React.lazy(() => import('./pages/Accounting/DebtManagement'));
const InvestorManagement = React.lazy(() => import('./pages/Partners/InvestorManagement'));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    ErrorLogger.logError(error, {
      component: 'App',
      errorInfo: errorInfo?.componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-5">
          <div className="alert alert-danger text-center">
            <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
            <h4>Đã xảy ra lỗi</h4>
            <p>Ứng dụng gặp lỗi không mong muốn. Vui lòng tải lại trang.</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <i className="fas fa-redo me-2"></i>Tải lại trang
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}




function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const { user, isAdmin } = useAuth();
  const { showNotification } = useNotification();
  const [isMaintenance, setIsMaintenance] = React.useState(false);

  // Check for maintenance mode
  React.useEffect(() => {
    let _isMounted = true;
    const checkMaintenance = async () => {
      try {
        const resp = await axios.get('/settings');
        if (resp.status === 200) {
          const result = resp.data;
          if (result.success && result.data && _isMounted) {
            const settings = result.data;
            const userIsAdmin = user && typeof isAdmin === 'function' && isAdmin();
            if (settings.maintenance_mode === true && !userIsAdmin) {
              setIsMaintenance(true);
            } else {
              setIsMaintenance(false);
            }
          }
        }
      } catch (e) {
        // silently fallback
      }
    };
    checkMaintenance();

    // Polling interval in case an admin enables it while a non-admin is active
    const pollInterval = setInterval(checkMaintenance, 10000); // Check every 10 seconds
    return () => {
      _isMounted = false;
      clearInterval(pollInterval);
    };
  }, [user, isAdmin]);

  // Check for app updates
  useEffect(() => {
    const handleOnline = () => {
      showNotification('Đã kết nối internet', 'success');
    };

    const handleOffline = () => {
      showNotification('Mất kết nối internet. Một số tính năng có thể bị hạn chế.', 'warning');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showNotification]);

  return (
    <ErrorBoundary>
      <div className="App">
        <OfflineIndicator />


        {isMaintenance && window.location.pathname !== '/login' ? (
          <Suspense fallback={<LoadingSpinner />}>
            <MaintenanceMode />
          </Suspense>
        ) : user ? (
          <div>
            <Sidebar />
            <div className="main-content">
              <div className="page-container">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
                    <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
                    <Route path="/customers" element={<ProtectedRoute permission={PERMISSIONS.CUSTOMER_VIEW}><Customers /></ProtectedRoute>} />
                    <Route path="/customers/:id" element={<ProtectedRoute permission={PERMISSIONS.CUSTOMER_VIEW}><CustomerDetail /></ProtectedRoute>} />
                    <Route path="/investors" element={<ProtectedRoute permission={PERMISSIONS.PARTNER}><InvestorManagement /></ProtectedRoute>} />
                    <Route path="/documents" element={<ProtectedRoute permission={PERMISSIONS.DOCUMENT_VIEW}><Documents /></ProtectedRoute>} />
                    <Route path="/reports" element={<ProtectedRoute permission={PERMISSIONS.REVENUE}><Reports /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute permission={PERMISSIONS.SETTINGS}><Settings /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/users" element={<ProtectedRoute permission={PERMISSIONS.ACCOUNT_MANAGEMENT}><UserManagement /></ProtectedRoute>} />
                    <Route path="/admin/activity-logs" element={<ProtectedRoute role="admin"><ActivityLogs /></ProtectedRoute>} />
                    <Route path="/admin/approvals" element={<ProtectedRoute permission={[PERMISSIONS.MY_REQUEST, PERMISSIONS.APPROVAL_REQUIRED]}><ApprovalCenter /></ProtectedRoute>} />
                    <Route path="/approvals/:id" element={<ProtectedRoute permission={[PERMISSIONS.MY_REQUEST, PERMISSIONS.APPROVAL_REQUIRED]}><ApprovalDetails /></ProtectedRoute>} />
                    <Route path="/utilities/readings" element={<ProtectedRoute permission={PERMISSIONS.ELECTRIC_WATER_VIEW}><UtilityManagement /></ProtectedRoute>} />
                    <Route path="/utilities/contract-studio" element={<ProtectedRoute><ContractStudio /></ProtectedRoute>} />
                    <Route path="/accounting/debts" element={<ProtectedRoute><DebtManagement /></ProtectedRoute>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </div>
            </div>
          </div>
        ) : (
          <div className="public-layout">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={1500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;

